import { useState, useEffect, useMemo, useCallback } from 'react';

import { UseQueryResult } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory, useLocation } from 'react-router-dom';

import FolderNamingDialog from 'domains/core/components/folders/FolderNamingDialog';
import { Segment } from 'domains/segments/types';
import useCreateSegmentFolder from 'hooks/mutations/useCreateSegmentFolder';
import useArchivedSegmentsList from 'hooks/queries/useArchivedSegmentsList';
import usePaginatedArchivedSegments from 'hooks/queries/usePaginatedArchivedSegments';
import usePaginatedSegmentsFolder from 'hooks/queries/usePaginatedSegmentsFolder';
import useSegmentsWithFolders from 'hooks/queries/useSegmentsWithFolders';
import useSegmentQueryManager from 'hooks/useSegmentQueryManager';
import { SortDirection, URLPaths } from 'models/enums';
import { Folder } from 'models/types';
import { ACCESS_CENTRE_LIST_PAGE_SIZE, ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS } from 'utils';
import { isRootFolder } from 'utils/folders';

import SegmentsPageLayout from './SegmentsPageLayout';

const SegmentsPageWithFolders = () => {
    const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);
    const [isArchivedView, setIsArchivedView] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const history = useHistory();
    const location = useLocation();
    const { folderId, changeViewParams } = useSegmentQueryManager();
    const { paginationFlag } = useFlags();
    const useListPagination = Boolean(paginationFlag);

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(ACCESS_CENTRE_LIST_PAGE_SIZE);
    const [sortColumnState, setSortColumnState] = useState('created_timestamp');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.DESCENDING);

    const offset = (page - 1) * rowsPerPage;

    const segmentsWithFoldersQuery = useSegmentsWithFolders({
        folderId,
        searchTerm,
        reactQueryOptions: { enabled: !useListPagination && !isArchivedView },
    });

    const paginatedSegmentsQuery = usePaginatedSegmentsFolder({
        options: {
            folderId,
            searchTerm,
            limit: rowsPerPage,
            offset,
            sortColumn: sortColumnState || undefined,
            sortDirection: sortColumnState ? sortDirectionState : undefined,
        },
        reactQueryOptions: { enabled: useListPagination && !isArchivedView, keepPreviousData: true },
    });

    const adaptedSegmentsQuery = useMemo(() => {
        if (!useListPagination) {
            return segmentsWithFoldersQuery as UseQueryResult<(Segment | Folder)[] | undefined, Error>;
        }
        return {
            ...paginatedSegmentsQuery,
            data: paginatedSegmentsQuery.data?.data ?? [],
        } as UseQueryResult<(Segment | Folder)[] | undefined, Error>;
    }, [useListPagination, segmentsWithFoldersQuery, paginatedSegmentsQuery]);

    const paginatedArchivedSegmentsQuery = usePaginatedArchivedSegments({
        options: {
            searchTerm,
            limit: rowsPerPage,
            offset,
            sortColumn: sortColumnState || undefined,
            sortDirection: sortColumnState ? sortDirectionState : undefined,
        },
        reactQueryOptions: { enabled: useListPagination && isArchivedView, keepPreviousData: true },
    });

    const adaptedPaginatedArchivedSegmentsQuery = useMemo(
        () =>
            ({
                ...paginatedArchivedSegmentsQuery,
                data: paginatedArchivedSegmentsQuery.data?.data ?? [],
            } as UseQueryResult<(Segment | Folder)[] | undefined, Error>),
        [paginatedArchivedSegmentsQuery]
    );

    const segmentsTotalCount = useListPagination
        ? isArchivedView
            ? paginatedArchivedSegmentsQuery.data?.total
            : paginatedSegmentsQuery.data?.total
        : undefined;
    const totalPages = Math.max(1, Math.ceil((segmentsTotalCount ?? 0) / rowsPerPage));

    useEffect(() => {
        if (!useListPagination) {
            return;
        }
        setPage(1);
    }, [useListPagination, folderId, searchTerm, isArchivedView]);

    useEffect(() => {
        if (!useListPagination) {
            return;
        }
        setPage(1);
    }, [useListPagination, rowsPerPage]);

    useEffect(() => {
        if (!useListPagination) {
            return;
        }
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [useListPagination, page, totalPages]);

    const handleSegmentSort = useCallback((sortColumn: string, direction: SortDirection) => {
        if (!sortColumn) {
            setSortColumnState('created_timestamp');
            setSortDirectionState(SortDirection.DESCENDING);
        } else {
            setSortColumnState(sortColumn);
            setSortDirectionState(direction);
        }
        setPage(1);
    }, []);
    const archivedSegmentsQuery = useArchivedSegmentsList({
        searchTerm,
        reactQueryOptions: { enabled: isArchivedView && !useListPagination },
    });

    // Check if we're on the archive URL and set state accordingly
    useEffect(() => {
        const isArchiveUrl = location.pathname === URLPaths.SEGMENTS_ARCHIVED;
        setIsArchivedView(isArchiveUrl);
    }, [location.pathname]);

    const refetchSegments = useCallback(() => {
        if (isArchivedView) {
            if (useListPagination) {
                return paginatedArchivedSegmentsQuery.refetch();
            }
            return archivedSegmentsQuery.refetch();
        }
        if (useListPagination) {
            return paginatedSegmentsQuery.refetch();
        }
        return segmentsWithFoldersQuery.refetch();
    }, [
        isArchivedView,
        useListPagination,
        paginatedArchivedSegmentsQuery,
        archivedSegmentsQuery,
        paginatedSegmentsQuery,
        segmentsWithFoldersQuery,
    ]);

    const isUserOnRootLevel = isRootFolder(folderId);

    const createFolder = (folderName: string) => {
        const body = {
            name: folderName,
        };
        saveFolder(body);
    };

    const onCreateFolderSuccess = (response: { id: number }) => {
        changeViewParams({ newFolderId: response.id });
        refetchSegments();
        setIsCreateFolderModalVisible(false);
    };

    const { mutate: saveFolder, isLoading: isFolderSaving } = useCreateSegmentFolder({
        onSuccess: onCreateFolderSuccess,
    });

    const handleToggleArchivedView = () => {
        const newArchivedView = !isArchivedView;
        setIsArchivedView(newArchivedView);

        // Update URL based on archived view state
        if (newArchivedView) {
            history.push(URLPaths.SEGMENTS_ARCHIVED);
        } else {
            // Use changeViewParams to properly handle folder paths
            changeViewParams({ newFolderId: folderId });
        }
    };

    const currentSegmentsQuery = isArchivedView
        ? useListPagination
            ? adaptedPaginatedArchivedSegmentsQuery
            : archivedSegmentsQuery
        : adaptedSegmentsQuery;

    const createButtonDropdownOptions = [
        { label: 'Audience', onClick: () => history.push(URLPaths.SEGMENT_CREATE) },
        { label: 'Folder', onClick: () => setIsCreateFolderModalVisible(true) },
    ];

    return (
        <>
            <FolderNamingDialog
                disableActionButton={isFolderSaving}
                initialValue="Untitled Folder"
                isDialogVisible={isCreateFolderModalVisible}
                onClose={() => setIsCreateFolderModalVisible(false)}
                onSubmit={createFolder}
                title="create new folder"
            />
            <SegmentsPageLayout
                createButtonDropdownOptions={createButtonDropdownOptions}
                isUserOnRootLevel={isUserOnRootLevel}
                segmentsQuery={currentSegmentsQuery}
                isArchivedView={isArchivedView}
                onToggleArchivedView={handleToggleArchivedView}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                segmentsPagination={
                    useListPagination
                        ? {
                              page,
                              totalPages,
                              onPreviousPage: () => setPage((p) => Math.max(1, p - 1)),
                              onNextPage: () => setPage((p) => Math.min(totalPages, p + 1)),
                              rowsPerPage,
                              rowsPerPageOptions: ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS,
                              onRowsPerPageChange: (n: number) => {
                                  setRowsPerPage(n);
                                  setPage(1);
                              },
                          }
                        : undefined
                }
                segmentsTotalCount={segmentsTotalCount}
                onSegmentSort={useListPagination ? handleSegmentSort : undefined}
                manualSegmentSortBy={useListPagination}
            />
        </>
    );
};

export default SegmentsPageWithFolders;
